import { CircleInfoIcon } from "@xola/icons";
import { Button, ComboBox, FormGroup, Label, Switch, Tooltip } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import clsx from "clsx";
import React from "react";
import { Experience, ScheduleOption } from "types";
import { getActiveOptions, getOptions, OptionChoice, toOptionChoices } from "@/models/Experience";

export const EXPERIENCE_OPTION_PRIORITY_MIN = 1;
export const EXPERIENCE_OPTION_PRIORITY_MAX = 5;

const PRIORITY_TOOLTIP =
    "Set the display priority (1 is lowest, 5 is highest). If two conflicting schedules apply to the same day or time, the one with the higher priority will be displayed to the customer. If two conflicting schedules have the same priority, then the most recently updated one will take priority.";

const PRIORITY_LEVELS = Array.from(
    { length: EXPERIENCE_OPTION_PRIORITY_MAX - EXPERIENCE_OPTION_PRIORITY_MIN + 1 },
    (_, index) => EXPERIENCE_OPTION_PRIORITY_MIN + index,
);

interface ExperienceOptionPickerProps {
    experience: Experience;
    // Capped at one entry by business rule. [] means explicitly detached.
    value?: ScheduleOption[];
    error?: string;
    onChange: (next: ScheduleOption[]) => void;
}

export const ExperienceOptionPicker = React.memo(
    ({ experience, value, error, onChange }: ExperienceOptionPickerProps) => {
        // The toggle writes a placeholder entry when turned on, so a non-empty value is exactly "overridden".
        const isOverridden = (value?.length ?? 0) > 0;
        // Archived Options are never offered for new attachment, but an already-
        // attached one must still resolve by name here even after archiving.
        const allOptions = getOptions(experience);
        const selectableOptions = getActiveOptions(experience);
        const selected = value?.[0];
        const selectedOption = allOptions.find((option) => option.id === selected?.optionId);

        const choices = toOptionChoices(selectableOptions);
        const selectedChoice = selectedOption ? { value: selectedOption.id, label: selectedOption.name } : null;

        const handleToggle = useMemoizedFn((enabled: boolean) => {
            onChange(enabled ? [{ optionId: "", priority: EXPERIENCE_OPTION_PRIORITY_MIN }] : []);
        });

        const handleOptionChange = useMemoizedFn((choice: OptionChoice | null) => {
            // Clearing the choice keeps the override on with an unchosen entry, so validation asks for one.
            onChange([
                { optionId: choice?.value ?? "", priority: selected?.priority ?? EXPERIENCE_OPTION_PRIORITY_MIN },
            ]);
        });

        const handlePriorityChange = useMemoizedFn((level: number) => {
            if (!selected) {
                return;
            }
            onChange([{ ...selected, priority: level }]);
        });

        return (
            <div className="mt-6 mb-4 space-y-3">
                <div className="flex items-center space-x-2">
                    <Switch.Group>
                        <Switch
                            size="medium"
                            data-testid="experience-option-override-toggle"
                            isChecked={isOverridden}
                            onChange={handleToggle}
                        />
                        <Switch.Label className="ml-2 text-sm font-bold">Override standard event details</Switch.Label>
                    </Switch.Group>
                </div>

                {isOverridden ? (
                    <>
                        <FormGroup>
                            <Label isError={!!error}>{error ?? "Experience Option"}</Label>
                            <ComboBox
                                data-testid="experience-option-picker"
                                options={choices}
                                value={selectedChoice}
                                placeholder="Select an Experience Option"
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 30 }) }}
                                onChange={handleOptionChange}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>
                                Priority{" "}
                                <Tooltip content={PRIORITY_TOOLTIP}>
                                    <CircleInfoIcon />
                                </Tooltip>
                            </Label>
                            <div className="flex items-center space-x-2">
                                {PRIORITY_LEVELS.map((level) => (
                                    <PriorityButton
                                        key={level}
                                        level={level}
                                        isSelected={(selected?.priority ?? EXPERIENCE_OPTION_PRIORITY_MIN) === level}
                                        onSelect={handlePriorityChange}
                                    />
                                ))}
                            </div>
                            <div className="mt-1 flex justify-between text-sm text-gray">
                                <span>Lowest</span>
                                <span>Highest</span>
                            </div>
                        </FormGroup>
                    </>
                ) : null}
            </div>
        );
    },
);
ExperienceOptionPicker.displayName = "ExperienceOptionPicker";

interface PriorityButtonProps {
    level: number;
    isSelected: boolean;
    onSelect: (level: number) => void;
}

const PriorityButton = ({ level, isSelected, onSelect }: PriorityButtonProps) => {
    const handleClick = useMemoizedFn(() => onSelect(level));

    return (
        <Button
            data-testid={`experience-option-priority-${level}`}
            className={clsx(
                isSelected && "bg-primary-lighter hover:bg-primary-lighter",
                "flex-1 text-black focus:ring-0",
            )}
            color={isSelected ? "primary" : "secondary"}
            variant="outline"
            size="small"
            onClick={handleClick}
        >
            {level}
        </Button>
    );
};
